import type {
  Categorie,
  Salarie,
  SituationFamiliale,
  StatutContrat,
  StatutSalarie,
} from '@shared/types/Salarie';
import { ExcelImporter } from '@infra/import/ExcelImporter';
import { assignMatriculesSequentiels } from '@domain/rh/ProchainMatricule';
import { RMM, SMIG } from '@shared/constants/Taux';
import { SalarieSchema } from '@shared/validation/SalarieSchema';

/** Noms des feuilles du modèle Excel (alignés sur les onglets fiche salarié). */
export const SALARIE_EXCEL_SHEETS = [
  'Identite',
  'Contrat',
  'Famille',
  'Medecine',
  'Paiement',
  'Urgence',
  'Sortie',
] as const;

export type SalarieExcelSheet = (typeof SALARIE_EXCEL_SHEETS)[number];

/** En-têtes CSV legacy (séparateur `;`) — sous-ensemble Identité + Contrat. */
export const SALARIE_CSV_HEADER = [
  'Matricule',
  'Nom',
  'Prénom',
  'Fonction',
  'Catégorie',
  'Salaire base',
  'Statut',
  'Contrat',
  'Embauche',
  'Email',
  'Téléphone',
  'CNSS',
  'CNAMGS',
  'NIF',
  'Date naissance',
  'Sexe',
  'Service',
  'Sursalaire',
  'Date_fin_contrat',
].join(';');

/** Colonnes obligatoires du modèle (feuille Identite). */
export const SALARIE_IMPORT_REQUIRED = [
  'Nom',
  'Prénom',
  'Fonction',
  'Catégorie',
  'Salaire base',
  'Embauche',
  'Email',
] as const;

export interface SalarieCsvDraft {
  matricule: string;
  nom: string;
  prenom: string;
  poste: string;
  categorie: Categorie;
  salaire_base: number;
  statut: StatutSalarie;
  statut_contrat: StatutContrat;
  date_embauche: string;
  email: string;
  telephone?: string;
  telephone_secondaire?: string;
  cnss?: string;
  cnamgs?: string;
  nif?: string;
  date_naissance?: string;
  lieu_naissance?: string;
  sexe?: 'M' | 'F';
  nationalite?: string;
  nom_jeune_fille?: string;
  type_piece?: 'CNI' | 'Passeport' | 'Carte sejour';
  cni?: string;
  piece_delivrance?: string;
  piece_validite?: string;
  piece_autorite?: string;
  adresse?: string;
  ville?: string;
  bp?: string;
  email_perso?: string;
  sejour_num?: string;
  sejour_type?: string;
  sejour_delivrance?: string;
  sejour_expiration?: string;
  permis_travail?: string;
  quota_etranger?: 'oui' | 'non' | 'na';
  service?: string;
  manager?: string;
  convention?: string;
  coefficient?: string;
  date_fin_contrat?: string;
  essai_fin?: string;
  sursalaire?: number;
  indemnite_transport?: number;
  indemnite_vehicule?: number;
  indemnite_logement?: number;
  indemnite_fonction?: number;
  indemnite_autres?: number;
  situation?: SituationFamiliale;
  regime_matrimonial?: string;
  date_mariage?: string;
  enfants?: number;
  personnes_charge?: number;
  conjoint_nom?: string;
  conjoint_prenom?: string;
  conjoint_nif?: string;
  conjoint_profession?: string;
  conjoint_salarie?: boolean;
  conjoint_cnamgs?: boolean;
  medecine_centre?: string;
  medecin_referent?: string;
  visite_derniere?: string;
  visite_prochaine?: string;
  aptitude?: Salarie['aptitude'];
  groupe_sanguin?: string;
  restrictions_medicales?: string;
  vaccinations?: string;
  mode_paiement?: Salarie['mode_paiement'];
  banque?: string;
  agence?: string;
  code_banque?: string;
  code_guichet?: string;
  num_compte?: string;
  cle_rib?: string;
  iban?: string;
  bic?: string;
  mobile_money?: string;
  contact_urgence_nom?: string;
  contact_urgence_lien?: string;
  contact_urgence_telephone?: string;
  contact_urgence_telephone_2?: string;
  contact_urgence_adresse?: string;
  diplome?: string;
  ecole?: string;
  annee_diplome?: number;
  niveau_etudes?: string;
  certifications?: string;
  niveau_anglais?: string;
  langues?: string;
  date_sortie?: string;
  date_reintegration?: string;
  motif_sortie?: string;
  motif_detail?: string;
  conges_solde?: number;
  /** Ligne source (1-indexée, en-tête = 1). */
  ligne?: number;
}

export interface SalarieCsvParseResult {
  drafts: SalarieCsvDraft[];
  errors: { ligne: number; message: string }[];
}

const CATEGORIES: Record<string, Categorie> = {
  ouvrier: 'Ouvrier',
  'ouvrier qualifie': 'Ouvrier qualifie',
  'ouvrier qualifié': 'Ouvrier qualifie',
  employe: 'Employe',
  employé: 'Employe',
  'employe qualifie': 'Employe qualifie',
  'employé qualifié': 'Employe qualifie',
  'agent de maitrise': 'Agent de maitrise',
  'agent de maîtrise': 'Agent de maitrise',
  cadre: 'Cadre',
  'cadre superieur': 'Cadre superieur',
  'cadre supérieur': 'Cadre superieur',
  direction: 'Direction',
  stagiaire: 'Stagiaire',
};

const STATUTS: Record<string, StatutSalarie> = {
  actif: 'actif',
  inactif: 'inactif',
  conge: 'conge',
  congé: 'conge',
  suspendu: 'suspendu',
  sortie: 'sortie',
  sorti: 'sortie',
};

const CONTRATS: Record<string, StatutContrat> = {
  cdi: 'CDI',
  cdd: 'CDD',
  essai: 'essai',
  stage: 'stage',
  stagiaire: 'stage',
  apprentissage: 'apprentissage',
  interim: 'interim',
  intérim: 'interim',
  sortie: 'sortie',
};

function parseStatutContrat(raw: string): StatutContrat | undefined {
  const t = raw
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
  if (!t) return undefined;
  const exact = CONTRATS[t];
  if (exact) return exact;
  if (t.startsWith('cdd') || t.includes('duree determinee')) return 'CDD';
  if (t.startsWith('cdi') || t.includes('duree indeterminee')) return 'CDI';
  if (t.includes('stage')) return 'stage';
  if (t.includes('apprentissage')) return 'apprentissage';
  if (t.includes('interim') || t.includes('interimaire')) return 'interim';
  if (t.includes('essai') || t.includes('periode d essai')) return 'essai';
  return undefined;
}

function contratCell(row: Record<string, string>): string {
  return cell(
    row,
    'contrat',
    'statut_contrat',
    'type contrat',
    'type de contrat',
    'type_contrat',
    'nature contrat',
  );
}

function dateFinContratCell(row: Record<string, string>): string {
  return cell(
    row,
    'date_fin_contrat',
    'date fin contrat',
    'fin contrat',
    'date fin',
    'date de fin',
    'date_fin',
    'fin de contrat',
    'echeance',
    'échéance',
  );
}

function cell(row: Record<string, string>, ...keys: string[]): string {
  const lower = new Map(
    Object.entries(row).map(([key, value]) => [key.trim().toLowerCase(), value]),
  );
  for (const key of keys) {
    const value = lower.get(key.toLowerCase())?.trim();
    if (value) return value;
  }
  return '';
}

function csvCell(value: string | number | undefined): string {
  const text = String(value ?? '');
  if (/[;"\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function optNum(raw: string): number | undefined {
  if (!raw.trim()) return undefined;
  const n = Number(raw.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n) : undefined;
}

function parseOuiNon(raw: string): boolean | undefined {
  const t = raw.trim().toLowerCase();
  if (!t) return undefined;
  if (['oui', 'o', 'yes', '1', 'true'].includes(t)) return true;
  if (['non', 'n', 'no', '0', 'false'].includes(t)) return false;
  return undefined;
}

/** Normalise une date Excel/CSV (YYYY-MM-DD ou JJ/MM/AAAA). */
function isCalendarIso(iso: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 0));
  return (
    dt.getUTCFullYear() === y && dt.getUTCMonth() === (m ?? 1) - 1 && dt.getUTCDate() === d
  );
}

export function normalizeImportDate(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  let iso = '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) iso = t;
  const fr = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(t);
  if (!iso && fr) {
    iso = `${fr[3]}-${fr[2]!.padStart(2, '0')}-${fr[1]!.padStart(2, '0')}`;
  }
  if (!iso) {
    const n = Number(t.replace(',', '.'));
    if (Number.isFinite(n) && n > 20000 && n < 60000) {
      const utc = Date.UTC(1899, 11, 30) + Math.round(n) * 86_400_000;
      const d = new Date(utc);
      const y = d.getUTCFullYear();
      const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
      const day = String(d.getUTCDate()).padStart(2, '0');
      iso = `${y}-${mo}-${day}`;
    }
  }
  return isCalendarIso(iso) ? iso : '';
}

/** « Marié(e) », « CÉLIBATAIRE », « Pacsé » → valeurs métier sans accent. */
function parseSituationFamiliale(raw: string): SituationFamiliale | undefined {
  const t = raw
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\(e\)|\(s\)/g, '')
    .trim();
  if (!t) return undefined;
  if (t.startsWith('celib')) return 'celibataire';
  if (t.startsWith('marie')) return 'marie';
  if (t.startsWith('divorce')) return 'divorce';
  if (t.startsWith('veu')) return 'veuf';
  if (t.startsWith('pacs')) return 'pacs';
  return undefined;
}

function parseSexe(raw: string): 'M' | 'F' | undefined {
  const t = raw.trim().toLowerCase();
  if (!t) return undefined;
  if (['m', 'h', 'homme', 'masculin'].includes(t)) return 'M';
  if (['f', 'femme', 'feminin', 'féminin'].includes(t)) return 'F';
  return undefined;
}

function draftKey(matricule: string, nom: string, prenom: string): string {
  const m = matricule.trim().toLowerCase();
  if (m) return `m:${m}`;
  return `n:${nom.trim().toLowerCase()}|${prenom.trim().toLowerCase()}`;
}

function findSheet(
  sheets: Record<string, Record<string, string>[]>,
  ...aliases: string[]
): Record<string, string>[] | undefined {
  const entries = Object.entries(sheets);
  for (const alias of aliases) {
    const hit = entries.find(([name]) => name.trim().toLowerCase() === alias.toLowerCase());
    if (hit) return hit[1];
  }
  // Accents / espaces
  for (const alias of aliases) {
    const norm = alias
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '');
    const hit = entries.find(([name]) =>
      name
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .includes(norm),
    );
    if (hit) return hit[1];
  }
  return undefined;
}

function indexByMatricule(
  rows: Record<string, string>[] | undefined,
): Map<string, { row: Record<string, string>; ligne: number }> {
  const map = new Map<string, { row: Record<string, string>; ligne: number }>();
  if (!rows) return map;
  rows.forEach((row, idx) => {
    const mat = cell(row, 'matricule');
    if (!mat) return;
    map.set(mat.trim().toLowerCase(), { row, ligne: idx + 2 });
  });
  return map;
}

function mergeRowOnto(
  draft: SalarieCsvDraft,
  row: Record<string, string>,
  sheet: SalarieExcelSheet | 'legacy',
): void {
  if (sheet === 'Identite' || sheet === 'legacy') {
    const tel2 = cell(row, 'téléphone secondaire', 'telephone secondaire', 'telephone_secondaire');
    if (tel2) draft.telephone_secondaire = tel2;
    const lieu = cell(row, 'lieu naissance', 'lieu_naissance');
    if (lieu) draft.lieu_naissance = lieu;
    const nat = cell(row, 'nationalité', 'nationalite');
    if (nat) draft.nationalite = nat;
    const njf = cell(row, 'nom jeune fille', 'nom_jeune_fille');
    if (njf) draft.nom_jeune_fille = njf;
    const tp = cell(row, 'type pièce', 'type piece', 'type_piece').toLowerCase();
    if (tp.includes('passeport')) draft.type_piece = 'Passeport';
    else if (tp.includes('sejour') || tp.includes('séjour')) draft.type_piece = 'Carte sejour';
    else if (tp.includes('cni') || tp) draft.type_piece = 'CNI';
    const cni = cell(row, 'cni', 'n° cni', 'numero piece', 'n° pièce');
    if (cni) draft.cni = cni;
    const pd = normalizeImportDate(cell(row, 'piece delivrance', 'piece_delivrance'));
    if (pd) draft.piece_delivrance = pd;
    const pv = normalizeImportDate(cell(row, 'piece validite', 'piece_validite', 'validité pièce'));
    if (pv) draft.piece_validite = pv;
    const pa = cell(row, 'piece autorite', 'piece_autorite', 'autorité');
    if (pa) draft.piece_autorite = pa;
    const adr = cell(row, 'adresse');
    if (adr) draft.adresse = adr;
    const ville = cell(row, 'ville', 'quartier');
    if (ville) draft.ville = ville;
    const bp = cell(row, 'bp', 'boite postale');
    if (bp) draft.bp = bp;
    const ep = cell(row, 'email perso', 'email_perso', 'e-mail personnel');
    if (ep) draft.email_perso = ep.toLowerCase();
    const sn = cell(row, 'sejour_num', 'n° séjour', 'n° carte sejour');
    if (sn) draft.sejour_num = sn;
    const st = cell(row, 'sejour_type', 'type sejour');
    if (st) draft.sejour_type = st;
    const sd = normalizeImportDate(cell(row, 'sejour_delivrance', 'sejour delivrance'));
    if (sd) draft.sejour_delivrance = sd;
    const se = normalizeImportDate(cell(row, 'sejour_expiration', 'sejour expiration'));
    if (se) draft.sejour_expiration = se;
    const pt = cell(row, 'permis_travail', 'permis travail');
    if (pt) draft.permis_travail = pt;
    const qe = cell(row, 'quota_etranger', 'quota étranger').toLowerCase();
    if (qe === 'oui' || qe === 'non' || qe === 'na') draft.quota_etranger = qe;
  }

  if (sheet === 'Contrat' || sheet === 'legacy') {
    const sc = parseStatutContrat(contratCell(row));
    if (sc) draft.statut_contrat = sc;
    const mgr = cell(row, 'manager');
    if (mgr) draft.manager = mgr;
    const conv = cell(row, 'convention');
    if (conv) draft.convention = conv;
    const coef = cell(row, 'coefficient');
    if (coef) draft.coefficient = coef;
    const dfc = normalizeImportDate(dateFinContratCell(row));
    if (dfc) draft.date_fin_contrat = dfc;
    const essai = normalizeImportDate(cell(row, 'essai_fin', 'fin essai'));
    if (essai) draft.essai_fin = essai;
    const it = optNum(cell(row, 'indemnite_transport', 'indemnité transport', 'transport'));
    if (it !== undefined) draft.indemnite_transport = it;
    const iv = optNum(cell(row, 'indemnite_vehicule', 'indemnité véhicule', 'vehicule', 'véhicule'));
    if (iv !== undefined) draft.indemnite_vehicule = iv;
    const il = optNum(cell(row, 'indemnite_logement', 'indemnité logement', 'logement'));
    if (il !== undefined) draft.indemnite_logement = il;
    const iff = optNum(cell(row, 'indemnite_fonction', 'indemnité fonction'));
    if (iff !== undefined) draft.indemnite_fonction = iff;
    const ia = optNum(cell(row, 'indemnite_autres', 'autres indemnités'));
    if (ia !== undefined) draft.indemnite_autres = ia;
  }

  if (sheet === 'Famille' || sheet === 'legacy') {
    const sit = parseSituationFamiliale(cell(row, 'situation', 'situation familiale'));
    if (sit) draft.situation = sit;
    const reg = cell(row, 'regime_matrimonial', 'régime matrimonial');
    if (reg) draft.regime_matrimonial = reg;
    const dm = normalizeImportDate(cell(row, 'date_mariage', 'date mariage'));
    if (dm) draft.date_mariage = dm;
    const enf = optNum(cell(row, 'enfants', 'nb enfants'));
    if (enf !== undefined) draft.enfants = enf;
    const pc = optNum(cell(row, 'personnes_charge', 'personnes à charge'));
    if (pc !== undefined) draft.personnes_charge = pc;
    const cn = cell(row, 'conjoint_nom', 'nom conjoint');
    if (cn) draft.conjoint_nom = cn;
    const cp = cell(row, 'conjoint_prenom', 'prénom conjoint');
    if (cp) draft.conjoint_prenom = cp;
    const cif = cell(row, 'conjoint_nif', 'nif conjoint');
    if (cif) draft.conjoint_nif = cif;
    const cpr = cell(row, 'conjoint_profession', 'profession conjoint');
    if (cpr) draft.conjoint_profession = cpr;
    const cs = parseOuiNon(cell(row, 'conjoint_salarie', 'conjoint salarié'));
    if (cs !== undefined) draft.conjoint_salarie = cs;
    const cc = parseOuiNon(cell(row, 'conjoint_cnamgs', 'conjoint cnamgs'));
    if (cc !== undefined) draft.conjoint_cnamgs = cc;
  }

  if (sheet === 'Medecine' || sheet === 'legacy') {
    const mc = cell(row, 'medecine_centre', 'centre médecine');
    if (mc) draft.medecine_centre = mc;
    const mr = cell(row, 'medecin_referent', 'médecin référent');
    if (mr) draft.medecin_referent = mr;
    const vd = normalizeImportDate(cell(row, 'visite_derniere', 'dernière visite'));
    if (vd) draft.visite_derniere = vd;
    const vp = normalizeImportDate(cell(row, 'visite_prochaine', 'prochaine visite'));
    if (vp) draft.visite_prochaine = vp;
    const ap = cell(row, 'aptitude').toLowerCase();
    if (ap === 'apte' || ap === 'apte_restriction' || ap === 'inapte_temporaire' || ap === 'inapte_definitif') {
      draft.aptitude = ap;
    }
    const gs = cell(row, 'groupe_sanguin', 'groupe sanguin');
    if (gs) draft.groupe_sanguin = gs;
    const rm = cell(row, 'restrictions_medicales', 'restrictions');
    if (rm) draft.restrictions_medicales = rm;
    const vac = cell(row, 'vaccinations');
    if (vac) draft.vaccinations = vac;
  }

  if (sheet === 'Paiement' || sheet === 'legacy') {
    const mp = cell(row, 'mode_paiement', 'mode paiement').toLowerCase();
    if (mp === 'virement' || mp === 'cheque' || mp === 'especes' || mp === 'mobile_money') {
      draft.mode_paiement = mp;
    }
    const ban = cell(row, 'banque');
    if (ban) draft.banque = ban;
    const ag = cell(row, 'agence');
    if (ag) draft.agence = ag;
    const cb = cell(row, 'code_banque', 'code banque');
    if (cb) draft.code_banque = cb;
    const cg = cell(row, 'code_guichet', 'code guichet');
    if (cg) draft.code_guichet = cg;
    const nc = cell(row, 'num_compte', 'n° compte', 'compte');
    if (nc) draft.num_compte = nc;
    const cle = cell(row, 'cle_rib', 'clé rib');
    if (cle) draft.cle_rib = cle;
    const iban = cell(row, 'iban');
    if (iban) draft.iban = iban;
    const bic = cell(row, 'bic', 'swift');
    if (bic) draft.bic = bic;
    const mm = cell(row, 'mobile_money', 'mobile money');
    if (mm) draft.mobile_money = mm;
  }

  if (sheet === 'Urgence' || sheet === 'legacy') {
    const un = cell(row, 'contact_urgence_nom', 'urgence nom', 'contact urgence');
    if (un) draft.contact_urgence_nom = un;
    const ul = cell(row, 'contact_urgence_lien', 'urgence lien');
    if (ul) draft.contact_urgence_lien = ul;
    const ut = cell(row, 'contact_urgence_telephone', 'urgence téléphone', 'urgence tel');
    if (ut) draft.contact_urgence_telephone = ut;
    const ut2 = cell(row, 'contact_urgence_telephone_2', 'urgence téléphone 2');
    if (ut2) draft.contact_urgence_telephone_2 = ut2;
    const ua = cell(row, 'contact_urgence_adresse', 'urgence adresse');
    if (ua) draft.contact_urgence_adresse = ua;
    const dip = cell(row, 'diplome', 'diplôme');
    if (dip) draft.diplome = dip;
    const eco = cell(row, 'ecole', 'école');
    if (eco) draft.ecole = eco;
    const an = optNum(cell(row, 'annee_diplome', 'année diplôme'));
    if (an !== undefined) draft.annee_diplome = an;
    const ne = cell(row, 'niveau_etudes', 'niveau études');
    if (ne) draft.niveau_etudes = ne;
    const cert = cell(row, 'certifications');
    if (cert) draft.certifications = cert;
    const na = cell(row, 'niveau_anglais', 'anglais');
    if (na) draft.niveau_anglais = na;
    const lang = cell(row, 'langues');
    if (lang) draft.langues = lang;
  }

  if (sheet === 'Sortie' || sheet === 'legacy') {
    const ds = normalizeImportDate(cell(row, 'date_sortie', 'date sortie'));
    if (ds) draft.date_sortie = ds;
    const dr = normalizeImportDate(cell(row, 'date_reintegration', 'date réintégration', 'reintegration'));
    if (dr) draft.date_reintegration = dr;
    const ms = cell(row, 'motif_sortie', 'motif');
    if (ms) draft.motif_sortie = ms;
    const md = cell(row, 'motif_detail', 'commentaire sortie');
    if (md) draft.motif_detail = md;
    const cs = optNum(cell(row, 'conges_solde', 'solde congés'));
    if (cs !== undefined) draft.conges_solde = cs;
    const st = STATUTS[cell(row, 'statut').toLowerCase()];
    if (st) draft.statut = st;
  }
}

function draftFromRow(row: Record<string, string>, ligne: number): SalarieCsvDraft {
  const matricule = cell(row, 'matricule');
  const nom = cell(row, 'nom');
  const prenom = cell(row, 'prénom', 'prenom');
  const poste = cell(row, 'fonction', 'poste');
  const emailRaw = cell(row, 'email', 'e-mail').toLowerCase();
  const date_embauche = normalizeImportDate(
    cell(row, 'embauche', 'date_embauche', 'date embauche', "date d'embauche"),
  );
  const salaireRaw = cell(row, 'salaire base', 'salaire_base', 'salaire').replace(/\s/g, '');
  const salaire = Number(salaireRaw.replace(',', '.'));
  const categorieRaw = cell(row, 'catégorie', 'categorie').toLowerCase();
  const categorie = CATEGORIES[categorieRaw];
  const statut = STATUTS[cell(row, 'statut').toLowerCase()] ?? 'actif';
  const statut_contrat = parseStatutContrat(contratCell(row)) ?? 'CDI';
  const cnss = cell(row, 'cnss', 'n° cnss', 'numero cnss', 'numéro cnss') || undefined;
  const cnamgs = cell(row, 'cnamgs', 'n° cnamgs', 'numero cnamgs') || undefined;
  const nif = cell(row, 'nif') || undefined;
  const naissanceRaw = cell(row, 'date naissance', 'date_naissance', 'naissance');
  const date_naissance = naissanceRaw ? normalizeImportDate(naissanceRaw) : undefined;
  const sexe = parseSexe(cell(row, 'sexe'));
  const service = cell(row, 'service', 'département', 'departement') || undefined;
  const sursalaireRaw = cell(row, 'sursalaire').replace(/\s/g, '');
  const sursalaire = sursalaireRaw ? Number(sursalaireRaw.replace(',', '.')) : undefined;

  const manques: string[] = [];
  if (!nom) manques.push('nom');
  if (!prenom) manques.push('prénom');
  if (!poste) manques.push('fonction');
  if (emailRaw && !emailRaw.includes('@')) manques.push('email (format)');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date_embauche)) manques.push('embauche (AAAA-MM-JJ ou JJ/MM/AAAA)');
  if (naissanceRaw && !date_naissance) manques.push('date de naissance (date impossible)');
  if (!categorie) manques.push('catégorie');
  if (!Number.isFinite(salaire) || salaire < SMIG) manques.push(`salaire (≥ ${SMIG})`);
  if (sursalaire !== undefined && (!Number.isFinite(sursalaire) || sursalaire < 0)) {
    manques.push('sursalaire');
  }
  if (manques.length > 0 || !categorie) {
    throw new Error(`Ligne ${ligne} : ${manques.join(', ') || 'catégorie'}`);
  }

  const draft: SalarieCsvDraft = {
    matricule,
    nom,
    prenom,
    poste,
    categorie,
    salaire_base: Math.round(salaire),
    statut,
    statut_contrat,
    date_embauche,
    email: emailRaw,
    telephone: cell(row, 'téléphone', 'telephone') || undefined,
    cnss,
    cnamgs,
    nif,
    date_naissance,
    sexe,
    service,
    sursalaire:
      sursalaire !== undefined && Number.isFinite(sursalaire)
        ? Math.round(sursalaire)
        : undefined,
    ligne,
  };
  mergeRowOnto(draft, row, 'legacy');
  assertDraftMatchesSalarieSchema(draft, ligne);
  return draft;
}

function emptyToUndef(value: string | undefined): string | undefined {
  const t = (value ?? '').trim();
  return t.length > 0 ? t : undefined;
}

/** Rejette une ligne d’import dont les champs ne passent pas le schéma salarié. */
function assertDraftMatchesSalarieSchema(draft: SalarieCsvDraft, ligne: number): void {
  const parsed = SalarieSchema.safeParse({
    matricule: draft.matricule || 'M0000',
    nom: draft.nom,
    prenom: draft.prenom,
    poste: draft.poste,
    date_embauche: draft.date_embauche,
    categorie: draft.categorie,
    statut_contrat: draft.statut_contrat || 'CDI',
    date_fin_contrat: emptyToUndef(draft.date_fin_contrat),
    statut: draft.statut || 'actif',
    salaire_base: draft.salaire_base,
    sursalaire: draft.sursalaire,
    email: draft.email,
    numero_cnss: emptyToUndef(draft.cnss),
    cnamgs: emptyToUndef(draft.cnamgs),
    nif: emptyToUndef(draft.nif),
    telephone: emptyToUndef(draft.telephone),
    date_naissance: emptyToUndef(draft.date_naissance),
    sexe: draft.sexe,
    service: emptyToUndef(draft.service),
    nationalite: emptyToUndef(draft.nationalite),
    type_piece: draft.type_piece,
    cni: emptyToUndef(draft.cni),
  });
  if (!parsed.success) {
    const motif = parsed.error.issues[0]?.message ?? 'données invalides';
    throw new Error(`Ligne ${ligne} : ${motif}`);
  }
}

/** Modèle CSV : matricule vide = attribution automatique M0001, M0002… à l’import. */
export function salarieCsvTemplate(): string {
  const example = [
    '',
    'Obame',
    'Marie',
    'Comptable',
    'Employe',
    RMM,
    'actif',
    'CDI',
    '2024-01-15',
    'marie.obame@exemple.ga',
    '+241 06 00 00 000',
    '1234567890123',
    '1234567890123',
    '',
    '1990-05-12',
    'F',
    'Comptabilité',
    0,
    '',
  ]
    .map(csvCell)
    .join(';');
  return `${SALARIE_CSV_HEADER}\n${example}`;
}

/** Modèle Excel multi-onglets (tous les champs de la fiche salarié). */
export function buildSalarieExcelTemplate(): Promise<ArrayBuffer> {
  const exampleMat = '';
  const sheets: Record<string, Array<Record<string, string | number>>> = {
    Identite: [
      {
        Matricule: exampleMat,
        Nom: 'Obame',
        Prénom: 'Marie',
        'Nom jeune fille': '',
        Sexe: 'F',
        'Date naissance': '1990-05-12',
        'Lieu naissance': 'Libreville',
        Nationalité: 'Gabonaise',
        NIF: '',
        'Type pièce': 'CNI',
        CNI: 'A12345678',
        'Piece delivrance': '2018-01-10',
        'Piece validite': '',
        'Piece autorite': 'Préfecture Estuaire',
        CNSS: '1234567890123',
        CNAMGS: '1234567890123',
        Adresse: 'Quartier Glass',
        Ville: 'Libreville',
        BP: '',
        Email: 'marie.obame@exemple.ga',
        'Email perso': '',
        Téléphone: '+241 06 00 00 000',
        'Téléphone secondaire': '',
        Embauche: '2024-01-15',
        'Sejour_num': '',
        'Sejour_type': '',
        'Permis_travail': '',
        'Quota_etranger': 'na',
      },
    ],
    Contrat: [
      {
        Matricule: exampleMat,
        Fonction: 'Comptable',
        Catégorie: 'Employe',
        Contrat: 'CDI',
        Statut: 'actif',
        'Salaire base': RMM,
        Sursalaire: 0,
        Service: 'Comptabilité',
        Manager: '',
        Convention: 'Convention nationale interprofessionnelle',
        Coefficient: '',
        'Date_fin_contrat': '',
        'Essai_fin': '',
        'Indemnite_transport': 0,
        'Indemnite_logement': 0,
        'Indemnite_fonction': 0,
        'Indemnite_autres': 0,
      },
    ],
    Famille: [
      {
        Matricule: exampleMat,
        Situation: 'celibataire',
        'Regime_matrimonial': '',
        'Date_mariage': '',
        Enfants: 0,
        'Personnes_charge': 0,
        'Conjoint_nom': '',
        'Conjoint_prenom': '',
        'Conjoint_nif': '',
        'Conjoint_profession': '',
        'Conjoint_salarie': 'non',
        'Conjoint_cnamgs': 'oui',
      },
    ],
    Medecine: [
      {
        Matricule: exampleMat,
        'Medecine_centre': '',
        'Medecin_referent': '',
        'Visite_derniere': '',
        'Visite_prochaine': '',
        Aptitude: 'apte',
        'Groupe_sanguin': '',
        'Restrictions_medicales': '',
        Vaccinations: '',
      },
    ],
    Paiement: [
      {
        Matricule: exampleMat,
        'Mode_paiement': 'virement',
        Banque: 'BGFI Bank Gabon',
        Agence: '',
        'Code_banque': '',
        'Code_guichet': '',
        'Num_compte': '',
        'Cle_rib': '',
        IBAN: '',
        BIC: '',
        'Mobile_money': '',
      },
    ],
    Urgence: [
      {
        Matricule: exampleMat,
        'Contact_urgence_nom': '',
        'Contact_urgence_lien': '',
        'Contact_urgence_telephone': '',
        'Contact_urgence_telephone_2': '',
        'Contact_urgence_adresse': '',
        Diplome: '',
        Ecole: '',
        'Annee_diplome': '',
        'Niveau_etudes': '',
        Certifications: '',
        'Niveau_anglais': '',
        Langues: '',
      },
    ],
    Sortie: [
      {
        Matricule: exampleMat,
        Statut: 'actif',
        'Date_sortie': '',
        'Date_reintegration': '',
        'Motif_sortie': '',
        'Motif_detail': '',
        'Conges_solde': '',
      },
    ],
    _LisezMoi: [
      {
        Consigne:
          'Remplissez Identite + Contrat (champs obligatoires). Les autres onglets sont optionnels. Liez les lignes par Matricule (laissez vide pour attribution auto M0001…). Formats date : AAAA-MM-JJ ou JJ/MM/AAAA. CNI : 9 caractères alphanumériques.',
      },
    ],
  };
  return ExcelImporter.buildWorkbook(sheets);
}

export function salariesToCsv(salaries: readonly Salarie[]): string {
  const lines = salaries.map((s) =>
    [
      s.matricule,
      s.nom,
      s.prenom,
      s.poste ?? '',
      s.categorie,
      s.salaire_base,
      s.statut,
      s.statut_contrat ?? 'CDI',
      s.date_embauche,
      s.email ?? '',
      s.telephone ?? '',
      s.numero_cnss ?? s.cnss ?? '',
      s.cnamgs ?? '',
      s.nif ?? '',
      s.date_naissance ?? '',
      s.sexe ?? '',
      s.service ?? '',
      s.sursalaire ?? '',
      s.date_fin_contrat ?? '',
    ]
      .map(csvCell)
      .join(';'),
  );
  return [SALARIE_CSV_HEADER, ...lines].join('\n');
}

export function parseSalarieCsv(contenu: string): SalarieCsvParseResult {
  const parsed = ExcelImporter.parseCSV(contenu, (row, ligne) => draftFromRow(row, ligne));
  return { drafts: parsed.data, errors: parsed.errors };
}

/** Parse un classeur Excel (.xlsx) — multi-onglets si présent, sinon 1ʳᵉ feuille. */
export function parseSalarieExcel(buffer: ArrayBuffer): SalarieCsvParseResult {
  const sheets = ExcelImporter.workbookToSheetObjects(buffer);
  const sheetNames = Object.keys(sheets);
  const hasMulti = SALARIE_EXCEL_SHEETS.some((name) =>
    sheetNames.some((s) => s.toLowerCase().includes(name.toLowerCase().slice(0, 5))),
  );

  if (!hasMulti || sheetNames.length <= 1) {
    const rows = sheets[sheetNames[0] ?? ''] ?? [];
    const drafts: SalarieCsvDraft[] = [];
    const errors: { ligne: number; message: string }[] = [];
    rows.forEach((row, idx) => {
      const ligne = idx + 2;
      try {
        drafts.push(draftFromRow(row, ligne));
      } catch (err) {
        errors.push({ ligne, message: String(err instanceof Error ? err.message : err) });
      }
    });
    return { drafts, errors };
  }

  const identite =
    findSheet(sheets, 'Identite', 'Identité', 'Identite') ??
    findSheet(sheets, ...sheetNames) ??
    [];
  const contrat = findSheet(sheets, 'Contrat', 'Contrat & rémunération');
  const famille = findSheet(sheets, 'Famille', 'Famille & fiscalité');
  const medecine = findSheet(sheets, 'Medecine', 'Médecine', 'Medecine & securite');
  const paiement = findSheet(sheets, 'Paiement', 'Paiement & banque');
  const urgence = findSheet(sheets, 'Urgence', 'Urgence & diplômes');
  const sortie = findSheet(sheets, 'Sortie');

  const byMatContrat = indexByMatricule(contrat);
  const byMatFamille = indexByMatricule(famille);
  const byMatMed = indexByMatricule(medecine);
  const byMatPay = indexByMatricule(paiement);
  const byMatUrg = indexByMatricule(urgence);
  const byMatSortie = indexByMatricule(sortie);

  const drafts: SalarieCsvDraft[] = [];
  const errors: { ligne: number; message: string }[] = [];
  const seen = new Set<string>();

  identite.forEach((row, idx) => {
    const ligne = idx + 2;
    try {
      // Fusionne Contrat sur la même ligne Identité si matricule connu, sinon colonnes Contrat absentes
      const mat = cell(row, 'matricule').trim().toLowerCase();
      const merged: Record<string, string> = { ...row };
      const attach = (extra: { row: Record<string, string> } | undefined): void => {
        if (!extra) return;
        for (const [k, v] of Object.entries(extra.row)) {
          if (k.toLowerCase() === 'matricule') continue;
          if (!merged[k] && v) merged[k] = v;
        }
      };
      if (mat) {
        attach(byMatContrat.get(mat));
        attach(byMatFamille.get(mat));
        attach(byMatMed.get(mat));
        attach(byMatPay.get(mat));
        attach(byMatUrg.get(mat));
        attach(byMatSortie.get(mat));
      } else if (contrat?.[idx]) {
        attach({ row: contrat[idx]! });
      }

      // Si Fonction / Catégorie / Salaire sont sur Contrat uniquement
      if (!cell(merged, 'fonction', 'poste') && contrat) {
        const cRow = mat ? byMatContrat.get(mat)?.row : contrat[idx];
        if (cRow) {
          for (const [k, v] of Object.entries(cRow)) {
            if (!merged[k] && v) merged[k] = v;
          }
        }
      }

      const draft = draftFromRow(merged, ligne);
      const key = draftKey(draft.matricule, draft.nom, draft.prenom);
      if (seen.has(key)) {
        errors.push({ ligne, message: `Ligne ${ligne} : doublon (même matricule / identité)` });
        return;
      }
      seen.add(key);
      drafts.push(draft);
    } catch (err) {
      errors.push({ ligne, message: String(err instanceof Error ? err.message : err) });
    }
  });

  return { drafts, errors };
}

/** Complète les matricules manquants avant persistance (séquence M0001+). */
export function finalizeSalarieCsvDrafts(
  drafts: readonly SalarieCsvDraft[],
  existing: ReadonlyArray<{ matricule: string }>,
): SalarieCsvDraft[] {
  return assignMatriculesSequentiels(drafts, existing);
}

export function findExistingSalarie(
  draft: SalarieCsvDraft,
  byMatricule: Map<string, Salarie>,
  byCnss: Map<string, Salarie>,
  byEmail: Map<string, Salarie>,
): Salarie | undefined {
  const mat = draft.matricule.trim().toLowerCase();
  if (mat && byMatricule.has(mat)) return byMatricule.get(mat);
  const cnss = (draft.cnss ?? '').trim().toLowerCase();
  if (cnss && byCnss.has(cnss)) return byCnss.get(cnss);
  const email = draft.email.trim().toLowerCase();
  if (email && byEmail.has(email)) return byEmail.get(email);
  return undefined;
}

export function mergeSalarieDraft(existing: Salarie, draft: SalarieCsvDraft, email: string): Salarie {
  return {
    ...existing,
    matricule: draft.matricule || existing.matricule,
    nom: draft.nom,
    prenom: draft.prenom,
    poste: draft.poste,
    categorie: draft.categorie,
    salaire_base: draft.salaire_base,
    statut: draft.statut,
    statut_contrat: draft.statut_contrat,
    date_embauche: draft.date_embauche,
    email: email || existing.email,
    telephone: draft.telephone ?? existing.telephone,
    telephone_secondaire: draft.telephone_secondaire ?? existing.telephone_secondaire,
    cnss: draft.cnss ?? existing.cnss,
    numero_cnss: draft.cnss ?? existing.numero_cnss ?? existing.cnss,
    cnamgs: draft.cnamgs ?? existing.cnamgs,
    nif: draft.nif ?? existing.nif,
    date_naissance: draft.date_naissance ?? existing.date_naissance,
    lieu_naissance: draft.lieu_naissance ?? existing.lieu_naissance,
    sexe: draft.sexe ?? existing.sexe,
    nationalite: draft.nationalite ?? existing.nationalite,
    nom_jeune_fille: draft.nom_jeune_fille ?? existing.nom_jeune_fille,
    type_piece: draft.type_piece ?? existing.type_piece,
    cni: draft.cni ?? existing.cni,
    piece_delivrance: draft.piece_delivrance ?? existing.piece_delivrance,
    piece_validite: draft.piece_validite ?? existing.piece_validite,
    piece_autorite: draft.piece_autorite ?? existing.piece_autorite,
    adresse: draft.adresse ?? existing.adresse,
    ville: draft.ville ?? existing.ville,
    bp: draft.bp ?? existing.bp,
    email_perso: draft.email_perso ?? existing.email_perso,
    sejour_num: draft.sejour_num ?? existing.sejour_num,
    sejour_type: draft.sejour_type ?? existing.sejour_type,
    sejour_delivrance: draft.sejour_delivrance ?? existing.sejour_delivrance,
    sejour_expiration: draft.sejour_expiration ?? existing.sejour_expiration,
    permis_travail: draft.permis_travail ?? existing.permis_travail,
    quota_etranger: draft.quota_etranger ?? existing.quota_etranger,
    service: draft.service ?? existing.service,
    manager: draft.manager ?? existing.manager,
    convention: draft.convention ?? existing.convention,
    coefficient: draft.coefficient ?? existing.coefficient,
    date_fin_contrat: draft.date_fin_contrat ?? existing.date_fin_contrat,
    essai_fin: draft.essai_fin ?? existing.essai_fin,
    sursalaire: draft.sursalaire ?? existing.sursalaire,
    indemnite_transport: draft.indemnite_transport ?? existing.indemnite_transport,
    indemnite_vehicule: draft.indemnite_vehicule ?? existing.indemnite_vehicule,
    indemnite_logement: draft.indemnite_logement ?? existing.indemnite_logement,
    indemnite_fonction: draft.indemnite_fonction ?? existing.indemnite_fonction,
    indemnite_autres: draft.indemnite_autres ?? existing.indemnite_autres,
    situation: (draft.situation as Salarie['situation']) ?? existing.situation,
    regime_matrimonial: draft.regime_matrimonial ?? existing.regime_matrimonial,
    date_mariage: draft.date_mariage ?? existing.date_mariage,
    enfants: draft.enfants ?? existing.enfants,
    personnes_charge: draft.personnes_charge ?? existing.personnes_charge,
    conjoint_nom: draft.conjoint_nom ?? existing.conjoint_nom,
    conjoint_prenom: draft.conjoint_prenom ?? existing.conjoint_prenom,
    conjoint_nif: draft.conjoint_nif ?? existing.conjoint_nif,
    conjoint_profession: draft.conjoint_profession ?? existing.conjoint_profession,
    conjoint_salarie: draft.conjoint_salarie ?? existing.conjoint_salarie,
    conjoint_cnamgs: draft.conjoint_cnamgs ?? existing.conjoint_cnamgs,
    medecine_centre: draft.medecine_centre ?? existing.medecine_centre,
    medecin_referent: draft.medecin_referent ?? existing.medecin_referent,
    visite_derniere: draft.visite_derniere ?? existing.visite_derniere,
    visite_prochaine: draft.visite_prochaine ?? existing.visite_prochaine,
    aptitude: draft.aptitude ?? existing.aptitude,
    groupe_sanguin: draft.groupe_sanguin ?? existing.groupe_sanguin,
    restrictions_medicales: draft.restrictions_medicales ?? existing.restrictions_medicales,
    vaccinations: draft.vaccinations ?? existing.vaccinations,
    mode_paiement: draft.mode_paiement ?? existing.mode_paiement,
    banque: draft.banque ?? existing.banque,
    agence: draft.agence ?? existing.agence,
    code_banque: draft.code_banque ?? existing.code_banque,
    code_guichet: draft.code_guichet ?? existing.code_guichet,
    num_compte: draft.num_compte ?? existing.num_compte,
    cle_rib: draft.cle_rib ?? existing.cle_rib,
    iban: draft.iban ?? existing.iban,
    bic: draft.bic ?? existing.bic,
    mobile_money: draft.mobile_money ?? existing.mobile_money,
    contact_urgence_nom: draft.contact_urgence_nom ?? existing.contact_urgence_nom,
    contact_urgence_lien: draft.contact_urgence_lien ?? existing.contact_urgence_lien,
    contact_urgence_telephone:
      draft.contact_urgence_telephone ?? existing.contact_urgence_telephone,
    contact_urgence_telephone_2:
      draft.contact_urgence_telephone_2 ?? existing.contact_urgence_telephone_2,
    contact_urgence_adresse: draft.contact_urgence_adresse ?? existing.contact_urgence_adresse,
    diplome: draft.diplome ?? existing.diplome,
    ecole: draft.ecole ?? existing.ecole,
    annee_diplome: draft.annee_diplome ?? existing.annee_diplome,
    niveau_etudes: draft.niveau_etudes ?? existing.niveau_etudes,
    certifications: draft.certifications ?? existing.certifications,
    niveau_anglais: draft.niveau_anglais ?? existing.niveau_anglais,
    langues: draft.langues ?? existing.langues,
    date_sortie: draft.date_sortie ?? existing.date_sortie,
    date_reintegration: draft.date_reintegration ?? existing.date_reintegration,
    motif_sortie: draft.motif_sortie ?? existing.motif_sortie,
    motif_detail: draft.motif_detail ?? existing.motif_detail,
    conges_solde: draft.conges_solde ?? existing.conges_solde,
  };
}
